import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; // Import the autoTable function
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function AdminQuotation() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state for new quotation
  const [formData, setFormData] = useState({
    studentName: '',
    email: '',
    phone: '',
    address: '',
    courseId: '',
    courseName: '',
    fee: '',
    comments: '',
    validUntil: '',
    discountInfo: '',
    installmentInfo: ''
  });

  // Modal states
  const [quotationModalOpen, setQuotationModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [quotationsList, setQuotationsList] = useState([]);

  useEffect(() => {
    fetchCourses();
    fetchQuotations();
  }, []);

  const fetchCourses = async () => {
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) return;

      const response = await axios.get(`${apiUrl}/api/admin/courses`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });

      if (response.data.success) {
        setCourses(response.data.courses);
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchQuotations = async () => {
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) return;

      const response = await axios.get(`${apiUrl}/api/admin/quotations`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });

      if (response.data.success) {
        setQuotationsList(response.data.quotations);
      }
    } catch (err) {
      console.error('Error fetching quotations:', err);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    const selectedCourse = courses.find(c => c.id === parseInt(courseId));
    
    setFormData(prev => ({
      ...prev,
      courseId: courseId,
      courseName: selectedCourse ? selectedCourse.coursename : '',
      fee: selectedCourse ? selectedCourse.fees : ''
    }));
  };

  const openQuotationModal = () => {
    setFormData({
      studentName: '',
      email: '',
      phone: '',
      address: '',
      courseId: '',
      courseName: '',
      fee: '',
      comments: '',
      validUntil: '',
      discountInfo: '',
      installmentInfo: ''
    });
    setError('');
    setSuccess('');
    setQuotationModalOpen(true);
  };

  const closeQuotationModal = () => {
    setQuotationModalOpen(false);
  };

  const openViewModal = (quotation) => {
    setSelectedQuotation(quotation);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedQuotation(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.studentName || !formData.email || !formData.phone || !formData.courseId || !formData.fee) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('You must be logged in');
        return;
      }

      const quotationNumber = 'QTN-' + Date.now();
      
      const response = await axios.post(`${apiUrl}/api/admin/quotations`, {
        quotationNumber,
        ...formData,
        fee: parseFloat(formData.fee)
      }, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });

      if (response.data.success) {
        setSuccess('Quotation saved successfully');
        fetchQuotations();
        setTimeout(() => {
          closeQuotationModal();
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to save quotation');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving quotation');
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = (quotation) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const primaryColor = '#009ab4';
  const pageWidth = 210;
  const margin = 15;
  const contentWidth = pageWidth - (2 * margin);

  // Header with Logo and Company Info
  try {
    doc.addImage('/assets/images/logo/logo.jpg', 'JPEG', margin, margin, 40, 15);
  } catch (error) {
    console.log('Logo not found, continuing without logo');
  }

  // Colored header bar
  doc.setFillColor(primaryColor);
  doc.rect(margin, 35, contentWidth, 25, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('IFM Edu Academy Pvt Ltd.', margin + 5, 45);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('VSK TOWER, Opp. TVS Jn, Ernakulam, Kerala - 682019', margin + 5, 52);
  doc.text('Email: enquiry@ifmedu.in | Phone: +91 90486 48777', margin + 5, 57);

  // Quotation Title
  doc.setTextColor(primaryColor);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('FEE QUOTATION', margin, 75);

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');

  // Quotation Number and Date
  doc.text(`Quotation No: ${quotation.quotationNumber}`, margin, 85);
  doc.text(`Date: ${new Date(quotation.createdAt).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })}`, pageWidth - margin - 60, 85);

  if (quotation.validUntil) {
    doc.text(`Valid Until: ${new Date(quotation.validUntil).toLocaleDateString('en-IN')}`, pageWidth - margin - 60, 92);
  }

  // Student Details Section
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, 100, contentWidth, 8, 'F');
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.text('STUDENT DETAILS', margin + 3, 106);
  doc.setFont('helvetica', 'normal');

  let yPos = 115;
  doc.text(`Name: ${quotation.studentName}`, margin, yPos);
  doc.text(`Email: ${quotation.email}`, margin + 80, yPos);
  doc.text(`Phone: ${quotation.phone}`, margin + 150, yPos);

  yPos += 8;
  if (quotation.address) {
    doc.text(`Address: ${quotation.address}`, margin, yPos);
    yPos += 8;
  }

  // Course Details Section
  yPos += 5;
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, yPos, contentWidth, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.text('COURSE DETAILS', margin + 3, yPos + 6);
  doc.setFont('helvetica', 'normal');

  yPos += 10;
  
  // Format the fee properly - remove any spaces and format with commas
  const feeValue = parseFloat(quotation.fee).toFixed(2);
  const formattedFee = `INR ${feeValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

  // Use autoTable for course details with proper alignment
  autoTable(doc, {
    startY: yPos,
    head: [['Course Name', 'Course Fee (INR)']],
    body: [
      [quotation.courseName, formattedFee]
    ],
    theme: 'grid',
    headStyles: {
      fillColor: [0, 154, 180],
      textColor: [255, 255, 255],
      fontSize: 11,
      fontStyle: 'bold',
      halign: 'left',
      cellPadding: 5
    },
    bodyStyles: {
      fontSize: 10,
      cellPadding: 5
    },
    columnStyles: {
      0: { 
        cellWidth: 120,
        halign: 'left'
      },
      1: { 
        cellWidth: 60, 
        halign: 'right', // Right align the fee
        valign: 'middle'
      }
    },
    margin: { left: margin, right: margin },
    tableLineColor: [200, 200, 200],
    tableLineWidth: 0.5
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Additional Information
  if (quotation.discountInfo || quotation.installmentInfo || quotation.comments) {
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos, contentWidth, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('ADDITIONAL INFORMATION', margin + 3, yPos + 6);
    doc.setFont('helvetica', 'normal');

    yPos += 15;

    if (quotation.discountInfo) {
      doc.text('Discount Information:', margin, yPos);
      doc.setFont('helvetica', 'italic');
      doc.text(quotation.discountInfo, margin + 45, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 8;
    }

    if (quotation.installmentInfo) {
      doc.text('Installment Plan:', margin, yPos);
      doc.setFont('helvetica', 'italic');
      doc.text(quotation.installmentInfo, margin + 45, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 8;
    }

    if (quotation.comments) {
      doc.text('Comments:', margin, yPos);
      doc.setFont('helvetica', 'italic');
      
      const splitComments = doc.splitTextToSize(quotation.comments, contentWidth - 50);
      doc.text(splitComments, margin + 45, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += (splitComments.length * 6);
    }
  }

  // Terms and Conditions
  yPos += 10;
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, yPos, contentWidth, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.text('TERMS AND CONDITIONS', margin + 3, yPos + 6);
  doc.setFont('helvetica', 'normal');

  yPos += 15;
  const terms = [
    '1. Once paid, fees are non-refundable as per institute policy.',
    '2. The institute reserves the right to modify course schedules if necessary.'
  ];

  terms.forEach((term, index) => {
    doc.text(term, margin, yPos + (index * 6));
  });

  // Footer
  const footerY = 270;
  doc.setFillColor(primaryColor);
  doc.rect(margin, footerY, contentWidth, 15, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('This is a computer generated quotation and does not require a signature.', margin + 5, footerY + 5);
  doc.text('© 2025 IFM Edu Academy Pvt Ltd. All rights reserved.', margin + 5, footerY + 10);

  // Save the PDF
  doc.save(`Quotation_${quotation.studentName}_${quotation.quotationNumber}.pdf`);
};



  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Fee Quotations</span>
                </li>
              </ul>
            </div>
            <button
              className="btn btn-primary"
              onClick={openQuotationModal}
            >
              <i className="ph ph-plus me-2"></i>
              Create New Quotation
            </button>
          </div>

          {/* Quotations List */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">All Quotations</h5>
            </div>
            <div className="card-body">
              {quotationsList.length === 0 ? (
                <div className="alert alert-info">No quotations found. Create your first quotation!</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Quotation No.</th>
                        <th>Student Name</th>
                        <th>Course</th>
                        <th>Fee (₹)</th>
                        <th>Date</th>
                        <th>Valid Until</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotationsList.map((quotation) => (
                        <tr key={quotation.id}>
                          <td>{quotation.quotationNumber}</td>
                          <td>{quotation.studentName}</td>
                          <td>{quotation.courseName}</td>
                          <td>₹{parseFloat(quotation.fee).toFixed(2)}</td>
                          <td>{formatDate(quotation.createdAt)}</td>
                          <td>{quotation.validUntil ? formatDate(quotation.validUntil) : 'N/A'}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-info me-2"
                              onClick={() => openViewModal(quotation)}
                            >
                              <i className="ph ph-eye"></i> View
                            </button>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => generatePDF(quotation)}
                            >
                              <i className="ph ph-download"></i> PDF
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Create Quotation Modal */}
          {quotationModalOpen && (
            <div className="modal fade show d-block" tabIndex="-1" onClick={(e) => { if (e.target.className.includes('modal')) closeQuotationModal(); }}>
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Create New Fee Quotation</h5>
                    <button type="button" className="btn-close" onClick={closeQuotationModal}></button>
                  </div>
                  <div className="modal-body">
                    {error && <div className="alert alert-danger">{error}</div>}
                    {success && <div className="alert alert-success">{success}</div>}
                    
                    <form onSubmit={handleSubmit}>
                      <h6 className="mb-3">Student Information</h6>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label htmlFor="studentName" className="form-label">Student Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            id="studentName"
                            value={formData.studentName}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="email" className="form-label">Email *</label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label htmlFor="phone" className="form-label">Phone Number *</label>
                          <input
                            type="tel"
                            className="form-control"
                            id="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="address" className="form-label">Address</label>
                          <input
                            type="text"
                            className="form-control"
                            id="address"
                            value={formData.address}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <h6 className="mb-3 mt-4">Course Information</h6>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label htmlFor="courseId" className="form-label">Select Course *</label>
                          <select
                            className="form-select"
                            id="courseId"
                            value={formData.courseId}
                            onChange={handleCourseChange}
                            required
                          >
                            <option value="">Select a course</option>
                            {courses.map(course => (
                              <option key={course.id} value={course.id}>
                                {course.coursename} - ₹{course.fees}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="fee" className="form-label">Course Fee (₹) *</label>
                          <input
                            type="number"
                            className="form-control"
                            id="fee"
                            value={formData.fee}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label htmlFor="validUntil" className="form-label">Quotation Valid Until</label>
                          <input
                            type="date"
                            className="form-control"
                            id="validUntil"
                            value={formData.validUntil}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <h6 className="mb-3 mt-4">Additional Information</h6>
                      <div className="mb-3">
                        <label htmlFor="discountInfo" className="form-label">Discount Information</label>
                        <textarea
                          className="form-control"
                          id="discountInfo"
                          rows="2"
                          placeholder="Enter any discount details, special offers, etc."
                          value={formData.discountInfo}
                          onChange={handleInputChange}
                        ></textarea>
                      </div>

                      <div className="mb-3">
                        <label htmlFor="installmentInfo" className="form-label">Installment Plan</label>
                        <textarea
                          className="form-control"
                          id="installmentInfo"
                          rows="2"
                          placeholder="Describe installment options if available"
                          value={formData.installmentInfo}
                          onChange={handleInputChange}
                        ></textarea>
                      </div>

                      <div className="mb-3">
                        <label htmlFor="comments" className="form-label">Additional Comments</label>
                        <textarea
                          className="form-control"
                          id="comments"
                          rows="3"
                          placeholder="Any other information or remarks"
                          value={formData.comments}
                          onChange={handleInputChange}
                        ></textarea>
                      </div>

                      <div className="modal-footer px-0 pb-0">
                        <button type="button" className="btn btn-secondary" onClick={closeQuotationModal}>
                          Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                          {loading ? 'Saving...' : 'Save Quotation'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* View Quotation Modal */}
          {viewModalOpen && selectedQuotation && (
            <div className="modal fade show d-block" tabIndex="-1" onClick={(e) => { if (e.target.className.includes('modal')) closeViewModal(); }}>
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Quotation Details</h5>
                    <button type="button" className="btn-close" onClick={closeViewModal}></button>
                  </div>
                  <div className="modal-body">
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <strong>Quotation Number:</strong> {selectedQuotation.quotationNumber}
                      </div>
                      <div className="col-md-6">
                        <strong>Date:</strong> {formatDate(selectedQuotation.createdAt)}
                      </div>
                    </div>

                    <h6 className="mt-3">Student Details</h6>
                    <div className="row mb-3">
                      <div className="col-md-4">
                        <strong>Name:</strong> {selectedQuotation.studentName}
                      </div>
                      <div className="col-md-4">
                        <strong>Email:</strong> {selectedQuotation.email}
                      </div>
                      <div className="col-md-4">
                        <strong>Phone:</strong> {selectedQuotation.phone}
                      </div>
                    </div>
                    
                    {selectedQuotation.address && (
                      <div className="row mb-3">
                        <div className="col-12">
                          <strong>Address:</strong> {selectedQuotation.address}
                        </div>
                      </div>
                    )}

                    <h6 className="mt-3">Course Details</h6>
                    <div className="row mb-3">
                      <div className="col-md-8">
                        <strong>Course:</strong> {selectedQuotation.courseName}
                      </div>
                      <div className="col-md-4">
                        <strong>Fee:</strong> ₹{parseFloat(selectedQuotation.fee).toFixed(2)}
                      </div>
                    </div>

                    {selectedQuotation.validUntil && (
                      <div className="row mb-3">
                        <div className="col-12">
                          <strong>Valid Until:</strong> {formatDate(selectedQuotation.validUntil)}
                        </div>
                      </div>
                    )}

                    {(selectedQuotation.discountInfo || selectedQuotation.installmentInfo || selectedQuotation.comments) && (
                      <>
                        <h6 className="mt-3">Additional Information</h6>
                        {selectedQuotation.discountInfo && (
                          <div className="mb-2">
                            <strong>Discount:</strong> {selectedQuotation.discountInfo}
                          </div>
                        )}
                        {selectedQuotation.installmentInfo && (
                          <div className="mb-2">
                            <strong>Installment Plan:</strong> {selectedQuotation.installmentInfo}
                          </div>
                        )}
                        {selectedQuotation.comments && (
                          <div className="mb-2">
                            <strong>Comments:</strong> {selectedQuotation.comments}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => generatePDF(selectedQuotation)}
                    >
                      <i className="ph ph-download"></i> Download PDF
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={closeViewModal}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminQuotation;